import { useDispatch, useSelector } from 'react-redux';
import { useState, useMemo, useEffect } from 'react';
import { useJsApiLoader, GoogleMap, Marker, DirectionsService, DirectionsRenderer, DirectionsResult } from "@react-google-maps/api";
import { GOOGLE_MAPS_API_KEY } from '../../api';
import Resizable from '../Resizable';
import { toast } from 'react-toastify';

import './Maps.css';
import RouteScreen from './RouteScreen';
import DetailsScreen from './DetailsScreen';
import NavBar from './NavBar';
import RouteMap from './RouteMap';
import { finish, postQuery } from '../../store/inquerySlice';
import { unwrapResult } from '@reduxjs/toolkit';
import ExtraServices from './ExtraServices';


const GOOGLE_LIBRARIES = ['drawing', 'geometry', 'maps', 'places'];



function useIsMobile(threshold = 825) {
    const [isMobile, setIsMobile] = useState(window.innerWidth < threshold);
  
    useEffect(() => {
        const handler = () => setIsMobile(window.innerWidth < threshold);
        window.addEventListener('resize', handler);
        return () => window.removeEventListener('resize', handler);
    }, [threshold]);
  
    return isMobile;
}

const SidePanelHandle = (props) => {
    return <div ref={props.innerRef} className="side-panel-handle" {...props} />;
};

function Maps(props) {
    const isMobile = useIsMobile();
    const dispatch = useDispatch();

    const inquery = useSelector((state) => state.inquery);
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);

    // Add event listener for window resize
    useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const screens = useMemo(() => {
        return [
            [ 
                "Marsruut",
                <RouteScreen />
            ],
            [
                "Reisijad",
                <DetailsScreen />
            ],
            [
                "Lisateenused",
                <ExtraServices />
            ],
        ];
    }, []);

    const { isLoaded } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: GOOGLE_MAPS_API_KEY,
        libraries: GOOGLE_LIBRARIES
    });
    
    const [ screenIndex, setScreenIndex ] = useState(0);

    function tryToContinue() {
        console.log("tryToContinue");
        // Per screen validation logic
        if(screenIndex == 0) {
            console.log("screenIndex == 0");
            console.log("inquery.waypoints:", inquery.waypoints);
            // If route has unfilled 'depart' fields, show error
            if(inquery.waypoints.some((w, i, arr) => (i < (arr.length - 1) && !w.depart))) {
                toast.error("Palun täitke kõikide peatuste väljumisajad");
                return;
            }

            console.log("current time:", new Date())
            inquery.waypoints.forEach((w, i, arr) => {
                console.log("waypoint's depart:", new Date(w.depart * 1000))
            });

            // If any start times are in the past
            if(inquery.waypoints.some((w, i, arr) => (i < (arr.length - 1) && new Date(w.depart * 1000) < new Date()))) {
                toast.error("Väljumisaeg ei saa olla minevikus");
                return;
            }

            // If some waypoint's depart time is before the next waypoint's depart time
            if(inquery.waypoints.some((w, i, arr) => (i < (arr.length - 1) && i > 0 && new Date(w.depart * 1000) < new Date(arr[i - 1].depart * 1000)))) {
                toast.error("Väljumisaeg peab olema ajaliselt pärast eelmist");
                return;
            }

            // If route has only one stop
            if(inquery.waypoints.length == 1) {
                toast.error("Palun lisage vähemalt kaks teepunkti");
                return;
            }
        }
        console.log("tryToContinue end");

        // If all good, continue to next page
        setScreenIndex(i => Math.min(i + 1, screens.length - 1));
    }

    async function sendGTMEvent(email) {

        try {
            // Generate SHA256 hash
            const encoder = new TextEncoder();
            const data = encoder.encode(email.trim().toLowerCase());
            const hashBuffer = await crypto.subtle.digest('SHA-256', data);
            const hashArray = Array.from(new Uint8Array(hashBuffer));
            const emailSha256 = hashArray.map(byte => byte.toString(16).padStart(2, '0')).join('').toLowerCase();

            // Push event to dataLayer
            if (typeof window.dataLayer !== 'undefined') {
                window.dataLayer.push({
                    'event': 'generate_lead',
                    'email': emailSha256
                });
                console.log('\'generate_lead\' event pushed to dataLayer with hashed email:', emailSha256);
            } else {
                console.error('dataLayer is not defined. GTM snippet might be missing or not loaded.');
            }

        } catch (error) {
            console.error('Error generating SHA256 hash or pushing to dataLayer:', error);
        }
    }

    async function onPostQuery() {
        if (!inquery.passengers)    { toast.error("Palun sisestage reisijate arv"); setScreenIndex(1); return; }
        if (!inquery.fullname)      { toast.error("Palun sisestage täisnimi"); setScreenIndex(2); return; }
        if (!inquery.email)         { toast.error("Palun sisestage e-posti aadress"); setScreenIndex(2); return; }
        if (!inquery.phone)         { toast.error("Palun sisestage telefoninumber"); setScreenIndex(2); return; }

        const res = await unwrapResult(await dispatch(postQuery({})));
        if(!res) {
            setScreenIndex(0);
            return;
        }

        await dispatch(finish({}));
        await sendGTMEvent(inquery.email);
        toast.success("Päring edukalt edastatud!");
    }

    if(!isLoaded) return (<></>);

    return (
        <div id='inquery' className='row'>
            <Resizable direction={isMobile ? 'none' : 'horizontal'} handle={(handleAxis, ref) => <SidePanelHandle innerRef={ref} {...props} />}>
                <div className="side-panel column w100" style={isMobile ? { width: '100%', height: '100%' } : {}}>

                    <div className="column grow" style={{ overflowY: 'auto' }}>
                        <div className="grow">
                            { screens[screenIndex][1] }
                        </div>
                    </div>

                    <div className="w100 row center-cross g8 p16" style={{ borderTop: '1px solid #ddd' }}>

                        {windowWidth < 500 ? (
                            screenIndex > 0 && (
                                <button className="button black-button" onClick={() => setScreenIndex(screenIndex - 1)}>
                                    TAGASI
                                </button>
                            )
                        ) : (
                            screens.map((screen, index) => (
                                <div className="pointer" onClick={() => { setScreenIndex(index); }}>
                                    <p style={{ color: (screenIndex == index) ? '#4098c2' : '#444', paddingRight: 16, fontSize: 14 }}>{ index + 1 }. { screen[0] }</p>
                                </div>
                            ))
                        )}

                        { (screenIndex < screens.length - 1) && <button className="button black-button" style={{ marginLeft: 'auto' }} onClick={() => tryToContinue()}>
                            EDASI
                        </button>}

                        { (screenIndex == screens.length - 1) && <button className="button black-button" style={{ marginLeft: 'auto' }} onClick={() => onPostQuery()}>
                            SAADA ÄRA
                        </button>}
                    </div>

                    {/* { (screenIndex < screens.length - 1) && <button className="button row center-cross g16 p16" style={{ fontSize: 18 }} onClick={tryToContinue}>
                        <div className="column grow">
                            <b style={{ textAlign: 'left', fontSize: 18 }}>Edasi</b>
                            <p style={{ textAlign: 'left', fontSize: 16 }}>Siia saad hiljem tagasi tulla</p>
                        </div>
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 60 60" fill="#ccc">
                            <path d="M21.2132 8.7868L42.4264 30L21.2132 51.2132L16.9706 46.9706L33.9411 30L16.9706 13.0294L21.2132 8.7868Z" fill="currentColor"/>
                        </svg>
                    </button> }

                    { (screenIndex == screens.length - 1) && <button className="button row center-cross g16 p16" style={{ fontSize: 18 }} onClick={onPostQuery}>
                        <div className="column grow">
                            <b style={{ textAlign: 'left', fontSize: 18 }}>Valmis & lõpeta</b>
                            <p style={{ textAlign: 'left', fontSize: 16 }}>Esita päring</p>
                        </div>
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 60 60" fill="#ccc">
                            <path d="M21.2132 8.7868L42.4264 30L21.2132 51.2132L16.9706 46.9706L33.9411 30L16.9706 13.0294L21.2132 8.7868Z" fill="currentColor"/>
                        </svg>
                    </button> } */}
                </div>
            </Resizable>

            { (!isMobile) && (
                <RouteMap />
            )}
        </div>
    );
}

export default Maps;